import { type CSSProperties, memo, type ReactNode, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'clsx';
import { cva } from 'class-variance-authority';
import { getObjectPosition } from '@pkgs/components/utils/getImageObjectPosition';
import { type TFunction } from '@pkgs/components/providers';
import { useHandleImageError } from '@pkgs/components/hooks/useHandleImageError';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { DEFAULT_IMAGE, ImageAiType, type FocalPoint } from '@ev-tech/search-modules-api';

import { type ImageComponent, type UploadcareImageTransformationProps } from '../Image/DefaultImageComponent';

import styles from './CarouselImage.module.css';

type Props = UploadcareImageTransformationProps & {
  src: string;
  imageComponent: ImageComponent;
  fallbackImage?: string;
  inView: boolean;
  shouldPreloadImage?: boolean;
  alt: string;
  onError?: (e: SyntheticEvent<Element, Event>) => void;
  onClick?: (e?: React.MouseEvent<HTMLImageElement>) => void;
  carouselIndex?: number;
  aspectRatio?: `${number}/${number}`;
  sizes?: string;
  width?: number;
  height?: number;
  imageReference?: React.RefObject<HTMLImageElement>;
  lazyLoadImage?: boolean;
  className?: string;
  imageStyles?: CSSProperties;
  focalPoint?: FocalPoint;
  shouldHideImage?: boolean;
  carouselPreloadIndex?: number;
  aiType?: ImageAiType;
  aiBadge?: ReactNode;
  isViewMoreSlide?: boolean;
  translationResolver?: TFunction;
  // Reason: allow arbitrary data-* attributes (e.g. test selectors, state-based CSS
  // hooks) to be forwarded through to the rendered <img> via `...restImageProps`.
  [key: `data-${string}`]: string;
};

const CAROUSEL_INDEX_TO_PRELOAD_IMAGE = 2;

const getImageStyle = ({
  aspectRatio,
  dimension,
  focalPoint,
  imageStyles,
}: {
  aspectRatio?: `${number}/${number}`;
  dimension: { width: number; height: number };
  focalPoint?: FocalPoint;
  imageStyles?: CSSProperties;
}): CSSProperties =>
  ({
    '--carousel-image-aspect-ratio': aspectRatio,
    '--carousel-image-object-fit': dimension.height > dimension.width ? 'contain' : 'cover',
    '--carousel-image-object-position': dimension.height > dimension.width ? 'center' : getObjectPosition(focalPoint),
    ...imageStyles,
  }) as CSSProperties;

const getSlideStyle = ({ aspectRatio }: { aspectRatio?: `${number}/${number}` }): CSSProperties =>
  ({
    '--carousel-slide-aspect-ratio': aspectRatio,
  }) as CSSProperties;

const slideVariants = cva(styles.slide, {
  variants: {
    isViewMoreSlide: {
      true: styles.viewMoreSlide,
    },
  },
});

export const CarouselImage = memo(
  ({
    src,
    imageComponent: Image,
    fallbackImage = DEFAULT_IMAGE,
    inView,
    shouldPreloadImage,
    aspectRatio,
    lazyLoadImage = true,
    className,
    imageStyles,
    onError,
    shouldHideImage,
    carouselIndex = 0,
    imageReference,
    focalPoint,
    carouselPreloadIndex = CAROUSEL_INDEX_TO_PRELOAD_IMAGE,
    aiType,
    aiBadge,
    isViewMoreSlide,
    translationResolver: t,
    ...restImageProps
  }: Props) => {
    const [dimension, setDimension] = useState({ width: 0, height: 0 });
    const [shouldLoadImageInView, setShouldLoadImageInView] = useState(false);

    const { isError, setIsError, handleImageError } = useHandleImageError(src, fallbackImage, onError);

    const internalImageRef = useRef<HTMLImageElement | null>(null);

    const imageRef = imageReference || internalImageRef;

    const isLoading = useMemo(() => !shouldLoadImageInView && inView, [inView, shouldLoadImageInView]);

    const updateImageDimension = useCallback(
      (naturalWidth: number, naturalHeight: number) => {
        if (!shouldLoadImageInView) {
          if ((naturalWidth === 0 || naturalHeight === 0) && !isError) {
            setIsError(true);
          }

          if (dimension.width !== naturalWidth || dimension.height !== naturalHeight || !isError) {
            setDimension({ width: naturalWidth, height: naturalHeight });
          }
          setShouldLoadImageInView(true);
        }
      },
      [dimension, isError, shouldLoadImageInView, setIsError],
    );

    const handleImageLoad = useCallback(
      (img: SyntheticEvent<HTMLImageElement, Event>) =>
        updateImageDimension(img.currentTarget.naturalWidth, img.currentTarget.naturalHeight),
      [updateImageDimension],
    );

    // This effect is used to update the image properties when the image is already loaded and onload is not triggered i.e client caching
    useEffect(() => {
      if (imageRef.current) {
        const imageElement = imageRef.current;

        if (imageElement.complete) {
          updateImageDimension(imageElement.naturalWidth, imageElement.naturalHeight);
        }
      }
    }, [src, updateImageDimension, imageRef]);
    return (
      <div className={cn(slideVariants({ isViewMoreSlide }), className)} style={getSlideStyle({ aspectRatio })}>
        {(inView || !lazyLoadImage) && !shouldHideImage && (
          <Image
            {...restImageProps}
            className={styles.image}
            ref={imageRef}
            onError={handleImageError}
            src={isError ? fallbackImage : src}
            style={getImageStyle({ aspectRatio, dimension, focalPoint, imageStyles })}
            {...(carouselIndex === 0 && shouldPreloadImage && { fetchPriority: 'high' })}
            loading={carouselIndex < carouselPreloadIndex && shouldPreloadImage ? 'eager' : 'lazy'}
            data-src={src}
            onLoad={handleImageLoad}
          />
        )}
        {isViewMoreSlide && t && <Copy className={styles.viewMoreLink} size="m" text={t('search.results.listingCard.viewMore.label')} />}
        {(aiType === ImageAiType.GENERATED || aiType === ImageAiType.MODIFIED) && aiBadge}
        <div className={styles.skeleton} data-visible={isLoading && lazyLoadImage} />
      </div>
    );
  },
);

CarouselImage.displayName = 'CarouselImage';
